// isValidEmail is a deliberately coarse client-side shape check — a single @
// with non-empty parts and a dot in the domain. It exists to catch obvious
// typos (e.g. the TLD-less "fgdfgg@gmail" that HTML type="email" accepts per
// spec) without a backend round-trip. The backend's visitor.NormalizeEmail is
// the source of truth; anything this check passes may still be rejected there,
// so this intentionally does NOT try to mirror the backend's exact rules.
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

// Suggestion targets, most popular first (ties resolve to the earliest entry).
//
// MIRROR: the suggestion logic below (domain lists, threshold, domainDistance,
// suggestEmailCorrection) is duplicated in staticsol at
// node_js/ezauth/src/emailtypo.ts. The two repos are separately-deployed
// runtime bundles with no shared build-time module graph, so the copy is
// deliberate — keep both in sync.
const POPULAR_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'icloud.com',
  'aol.com',
  'msn.com',
  'live.com',
  'me.com',
  'mac.com',
  'comcast.net',
  'googlemail.com',
  'protonmail.com',
  'ymail.com',
  'rocketmail.com',
  'att.net',
  'verizon.net',
  'sbcglobal.net',
];

// Real domains that sit within the suggestion threshold of a popular one
// (mail.com vs gmail.com, email.com vs gmail.com, …). Exact matches against
// this set never produce a suggestion, so users of these providers are not
// told their valid address looks wrong.
const KNOWN_REAL_DOMAINS = new Set([
  ...POPULAR_DOMAINS,
  'mail.com',
  'email.com',
  'gmx.com',
  'gmx.net',
  'proton.me',
  'pm.me',
  'zoho.com',
  'fastmail.com',
  'yahoo.co.uk',
  'yahoo.ca',
  'yahoo.com.au',
  'hotmail.co.uk',
  'hotmail.com.au',
  'live.co.uk',
  'live.com.au',
]);

// Two edits covers the realistic typo space (gmail.con, gmial.com, gmal.com,
// gmail.comm) without reaching unrelated real domains.
const SUGGESTION_THRESHOLD = 2;

// Optimal-string-alignment (Damerau) distance: insertion, deletion,
// substitution, and adjacent transposition each cost 1. Domains are short, so
// the O(m*n) table is irrelevant to bundle/runtime cost.
function domainDistance(a: string, b: string): number {
  const d: number[][] = [];
  for (let i = 0; i <= a.length; i++) {
    d[i] = [i];
  }
  for (let j = 0; j <= b.length; j++) {
    d[0][j] = j;
  }
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + cost);
      }
    }
  }
  return d[a.length][b.length];
}

// suggestEmailCorrection returns the input with its domain replaced by a
// popular lookalike when the typed domain is a near-miss ("gmail.con" →
// "gmail.com"), or null when the domain is known-real, unrecognized, or the
// input has no usable local@domain shape. Non-blocking by design: callers
// show the result as a "Did you mean …?" hint and still accept the original.
export function suggestEmailCorrection(email: string): string | null {
  const trimmed = email.trim();
  const at = trimmed.lastIndexOf('@');
  if (at <= 0 || at === trimmed.length - 1) {
    return null;
  }
  const local = trimmed.slice(0, at);
  const domain = trimmed.slice(at + 1).toLowerCase();
  if (!domain.includes('.') || KNOWN_REAL_DOMAINS.has(domain)) {
    return null;
  }
  let best: string | null = null;
  let bestDistance = SUGGESTION_THRESHOLD + 1;
  for (const target of POPULAR_DOMAINS) {
    const distance = domainDistance(domain, target);
    if (distance < bestDistance) {
      best = target;
      bestDistance = distance;
    }
  }
  return best ? `${local}@${best}` : null;
}
